import React from 'react';
import { cn } from './cn';

/**
 * A switch, and the row it usually lives in.
 *
 * Built on a real `<button role="switch">` rather than a styled checkbox: the
 * checkbox approach needs `appearance:none` plus a pseudo-element for the
 * knob, which is a CSS component class by another name, and screen readers
 * announce "switch, on" for this and "checkbox, checked" for that.
 *
 * ### The tap target is the button; the track is INSIDE it
 *
 * The track used to be the button itself, which meant the global
 * `(pointer: coarse)` floor — `button { min-height: 44px }` — applied to the
 * thing you can see. So on every phone the switch drew as a 52×44 slab, in a
 * form that stacks several of them. It looked like a bug and it was the touch
 * floor doing its job on the wrong element.
 *
 * Now the button is a transparent target that still takes the floor (and gets
 * a little horizontal padding so the target is comfortable at both ends),
 * while the visible track is a 46×28 span within it. Nothing here sets an
 * explicit `min-height`: that outranks the floor, which is how the same
 * regression landed three times before.
 *
 * On a mouse the button has no floor, so the control is simply 28px tall —
 * denser, which is the right trade-off with a cursor.
 */
export const Toggle = React.forwardRef(function Toggle(
  { checked, onChange, disabled, label, className, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={cn(
        'group inline-flex shrink-0 items-center justify-center rounded-full px-1',
        'border-0 bg-transparent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        disabled && 'cursor-not-allowed opacity-40',
        className
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          'relative inline-flex h-7 w-[46px] items-center rounded-full p-[3px]',
          'border transition-colors duration-150',
          checked
            ? 'border-accent bg-accent'
            : 'border-line bg-surface-sunken group-hover:border-line-strong'
        )}
      >
        <span
          className={cn(
            'block size-[22px] rounded-full transition-transform duration-150',
            'ease-[cubic-bezier(.22,.61,.36,1)]',
            checked ? 'translate-x-[18px] bg-accent-ink' : 'translate-x-0 bg-ink-subtle'
          )}
        />
      </span>
    </button>
  );
});

/**
 * Label + description on the left, control on the right.
 *
 * `min-w-0` on the text column is the flex trap: without it the description
 * refuses to wrap below its longest word and pushes the switch off-screen on
 * a phone, while `document.scrollWidth` reports no overflow at all because
 * `body { overflow-x: hidden }` hides the evidence.
 */
export const ToggleRow = ({ title, description, children, className }) => (
  <div className={cn('flex items-center justify-between gap-4 py-2', className)}>
    <div className="min-w-0">
      <p className="text-body-sm font-medium text-ink">{title}</p>
      {description ? (
        <p className="mt-1 text-caption text-ink-muted">{description}</p>
      ) : null}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default Toggle;
